import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import useAuth from '../hooks/useAuth';

const TECNOLOGIAS = ['ONU/ONT', 'Radio', 'Router', 'Switch', 'TV Box', 'Sin Registro'];

const EMPTY_FORM = {
    marca_id: '',
    nombre: '',
    tecnologia: '',
    cantidad_puertos: 0,
    wifi_2_4ghz: false,
    wifi_5_8ghz: false,
    wifi_6: false,
    pot: false,
    catv: false,
    para_usuario_final: false,
    ip_defecto: '192.168.1.1',
    puerto_http: 80,
    usuario_defecto: 'admin',
    clave_defecto: 'admin'
};

const TECNOLOGIA_COLOR = {
    'ONU/ONT': 'bg-blue-100 text-blue-700',
    'Radio': 'bg-purple-100 text-purple-700',
    'Router': 'bg-green-100 text-green-700',
    'Switch': 'bg-yellow-100 text-yellow-700',
    'TV Box': 'bg-red-100 text-red-700',
    'Sin Registro': 'bg-gray-100 text-gray-500'
};

const Modelos = () => {
    const { can, user } = useAuth();
    const [items, setItems] = useState([]);
    const [marcas, setMarcas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [editingId, setEditingId] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState('');
    const [filtroTecnologia, setFiltroTecnologia] = useState('');

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [modRes, marRes] = await Promise.all([
                api.get('/inventario/modelos'),
                api.get('/inventario/marcas')
            ]);
            setItems(modRes.data);
            setMarcas(marRes.data);
        } catch {
            setError('Error cargando datos');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { if (user) loadData(); }, [loadData, user]);

    const showSuccess = (msg) => {
        setSuccess(msg); setError('');
        setTimeout(() => setSuccess(''), 4000);
    };

    const openCreate = () => {
        setForm(EMPTY_FORM);
        setEditingId(null);
        setError('');
        setModalOpen(true);
    };

    const openEdit = (item) => {
        setForm({
            marca_id: item.marca_id,
            nombre: item.nombre,
            tecnologia: item.tecnologia,
            cantidad_puertos: item.cantidad_puertos,
            wifi_2_4ghz: !!Number(item.wifi_2_4ghz),
            wifi_5_8ghz: !!Number(item.wifi_5_8ghz),
            wifi_6: !!Number(item.wifi_6),
            pot: !!Number(item.pot),
            catv: !!Number(item.catv),
            para_usuario_final: !!Number(item.para_usuario_final),
            ip_defecto: item.ip_defecto || '192.168.1.1',
            puerto_http: item.puerto_http || 80,
            usuario_defecto: item.usuario_defecto || 'admin',
            clave_defecto: item.clave_defecto || 'admin'
        });
        setEditingId(item.id);
        setError('');
        setModalOpen(true);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSubmit = async () => {
        setSaving(true);
        setError('');
        try {
            if (editingId) {
                await api.put(`/inventario/modelos/${editingId}`, form);
                showSuccess('Modelo actualizado correctamente');
            } else {
                await api.post('/inventario/modelos', form);
                showSuccess('Modelo creado correctamente');
            }
            setModalOpen(false);
            loadData();
        } catch (err) {
            setError(err.response?.data?.error || 'Error guardando modelo');
        } finally {
            setSaving(false);
        }
    };

    const filtered = items.filter(i => {
        const matchSearch = `${i.marca_nombre} ${i.nombre}`.toLowerCase().includes(search.toLowerCase());
        const matchTec = !filtroTecnologia || i.tecnologia === filtroTecnologia;
        return matchSearch && matchTec;
    });

    const inputClass = "w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none";
    const labelClass = "block text-sm font-medium text-gray-700 mb-1";
    const toggleClass = "flex items-center gap-2 text-sm text-gray-600";

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-800">Modelos</h2>
                {can('inv_modelos', 'create') && (
                    <button onClick={openCreate} className="text-white text-sm font-semibold px-5 py-2.5 rounded-lg" style={{ backgroundColor: 'var(--color-primary)' }}>
                        + Agregar Nuevo
                    </button>
                )}
            </div>

            {success && <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg px-4 py-3 mb-4">{success}</div>}
            {error && !modalOpen && <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3 mb-4">{error}</div>}

            <div className="flex gap-3 mb-4">
                <input type="text" placeholder="Buscar por marca o modelo..." value={search} onChange={e => setSearch(e.target.value)}
                    className="w-full max-w-sm border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none" />
                <select value={filtroTecnologia} onChange={e => setFiltroTecnologia(e.target.value)}
                    className="border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none">
                    <option value="">Todas las tecnologías</option>
                    {TECNOLOGIAS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
            </div>

            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center text-gray-400 text-sm">Cargando...</div>
                ) : filtered.length === 0 ? (
                    <div className="p-8 text-center text-gray-400 text-sm">No se encontraron modelos</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                            <tr>
                                <th className="px-4 py-3 text-left">Marca</th>
                                <th className="px-4 py-3 text-left">Modelo</th>
                                <th className="px-4 py-3 text-left">Tecnología</th>
                                <th className="px-4 py-3 text-left">Puertos</th>
                                <th className="px-4 py-3 text-left">Equipos</th>
                                {can('inv_modelos', 'update') && <th className="px-4 py-3 text-right">Acciones</th>}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {filtered.map(item => (
                                <tr key={item.id} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 text-gray-600">{item.marca_nombre}</td>
                                    <td className="px-4 py-3 font-medium text-gray-800">{item.nombre}</td>
                                    <td className="px-4 py-3">
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TECNOLOGIA_COLOR[item.tecnologia] || 'bg-gray-100 text-gray-500'}`}>
                                            {item.tecnologia}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-gray-600">{item.cantidad_puertos}</td>
                                    <td className="px-4 py-3 text-gray-500">
                                        {item.equipos_instalados}/{item.equipos_total} instalados
                                    </td>
                                    {can('inv_modelos', 'update') && (
                                        <td className="px-4 py-3 text-right">
                                            <button onClick={() => openEdit(item)}
                                                className="text-xs font-medium px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50">
                                                Editar
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {modalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                            <h3 className="text-lg font-bold text-gray-800">{editingId ? 'Editar modelo' : 'Nuevo modelo'}</h3>
                            <button onClick={() => setModalOpen(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
                        </div>
                        <div className="p-6 space-y-4">
                            {error && <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3">{error}</div>}

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClass}>Tecnología *</label>
                                    <select name="tecnologia" value={form.tecnologia} onChange={handleChange} className={inputClass}>
                                        <option value="">Seleccionar...</option>
                                        {TECNOLOGIAS.map(t => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className={labelClass}>Marca *</label>
                                    <select name="marca_id" value={form.marca_id} onChange={handleChange} className={inputClass}>
                                        <option value="">Seleccionar...</option>
                                        {marcas.map(m => <option key={m.id} value={m.id}>{m.nombre}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClass}>Modelo *</label>
                                    <input name="nombre" value={form.nombre} onChange={handleChange} className={inputClass} placeholder="Ej: LBE-M5-23" />
                                </div>
                                <div>
                                    <label className={labelClass}>Puertos ETH</label>
                                    <input name="cantidad_puertos" type="number" min="0" value={form.cantidad_puertos} onChange={handleChange} className={inputClass} />
                                </div>
                            </div>

                            <div>
                                <label className={labelClass}>Capacidades</label>
                                <div className="grid grid-cols-3 gap-3 mt-1">
                                    {[
                                        ['wifi_2_4ghz', 'WiFi 2.4GHz'],
                                        ['wifi_5_8ghz', 'WiFi 5.8GHz'],
                                        ['wifi_6', 'WiFi 6'],
                                        ['pot', 'POT'],
                                        ['catv', 'CATV'],
                                        ['para_usuario_final', 'Usuario final'],
                                    ].map(([key, label]) => (
                                        <label key={key} className={toggleClass}>
                                            <input type="checkbox" name={key} checked={form[key]} onChange={handleChange} />
                                            {label}
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div className="border-t border-gray-100 pt-4">
                                <p className="text-xs text-gray-500 mb-3">Datos informativos para el técnico en la orden de servicio</p>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className={labelClass}>IP privada por defecto</label>
                                        <input name="ip_defecto" value={form.ip_defecto} onChange={handleChange} className={inputClass} />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Puerto HTTP</label>
                                        <input name="puerto_http" type="number" value={form.puerto_http} onChange={handleChange} className={inputClass} />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Usuario por defecto</label>
                                        <input name="usuario_defecto" value={form.usuario_defecto} onChange={handleChange} className={inputClass} />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Contraseña por defecto</label>
                                        <input name="clave_defecto" value={form.clave_defecto} onChange={handleChange} className={inputClass} />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="px-6 pb-6 flex gap-3">
                            <button onClick={() => setModalOpen(false)} className="flex-1 border border-gray-300 text-gray-700 font-semibold py-2.5 rounded-lg text-sm">Cancelar</button>
                            <button onClick={handleSubmit} disabled={saving}
                                className="flex-1 text-white font-semibold py-2.5 rounded-lg text-sm disabled:opacity-60"
                                style={{ backgroundColor: 'var(--color-primary)' }}>
                                {saving ? 'Guardando...' : (editingId ? 'Guardar cambios' : 'Crear modelo')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Modelos;
